import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from 'assets/icons/insights/close-icon.svg?react';
import { FC, memo, useCallback, useEffect } from 'react';
import { useAnalytics } from 'services/analytics';
import { RATING_STARS_COUNT, TRUSTPILOT_LINK } from 'utils';

import StarFilledIcon from '../../assets/star-filled.svg?react';
import {
  closeButtonSx,
  primaryButtonSx,
  secondaryActionSx,
  starContainerSx,
  titleSx,
  trustpilotBodySx,
} from './styles';

type TTrustpilotStepProps = {
  onClose: () => void;
  placement?: string;
  day?: number;
};

export const TrustpilotStep: FC<TTrustpilotStepProps> = memo(({ onClose, placement, day }) => {
  const { trackEvent } = useAnalytics();

  const handleHelp = useCallback((): void => {
    trackEvent('app_external_feedback_popup_click', {
      ...(placement && { placement }),
      ...(day && { day }),
      button: 'i_want_to_help',
    });
    window.open(TRUSTPILOT_LINK, '_blank');
    onClose();
  }, [trackEvent, placement, day, onClose]);

  const handleNoClick = useCallback((): void => {
    trackEvent('app_external_feedback_popup_click', {
      ...(placement && { placement }),
      ...(day && { day }),
      button: 'no',
    });
    onClose();
  }, [trackEvent, placement, day, onClose]);

  useEffect(() => {
    trackEvent('app_external_feedback_popup_shown', {
      ...(placement && { placement }),
      ...(day && { day }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack sx={{ gap: 6, alignItems: 'center' }}>
      <IconButton sx={closeButtonSx} onClick={onClose}>
        <CloseIcon />
      </IconButton>

      <Stack sx={starContainerSx}>
        {Array.from({ length: RATING_STARS_COUNT }).map((_, i) => (
          <Box key={i} display="flex" alignItems="center">
            <StarFilledIcon />
          </Box>
        ))}
      </Stack>

      <Stack sx={{ maxWidth: 300, gap: 12, alignItems: 'center' }}>
        <Stack sx={{ gap: 3, alignItems: 'center' }}>
          <Typography sx={titleSx}>
            Do you like us as much
            <br />
            as we like you?
          </Typography>

          <Typography sx={trustpilotBodySx}>
            We&apos;d love to get a detailed{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              5 stars comment
            </Box>{' '}
            from you on Trustpilot, telling the word why you enjoy LUMI.
          </Typography>
        </Stack>

        <Stack sx={{ width: 1, gap: 4, alignItems: 'center' }}>
          <Button sx={primaryButtonSx} onClick={handleHelp}>
            I want to help
          </Button>
          <Box component="button" sx={secondaryActionSx} onClick={handleNoClick}>
            No, I don&apos;t want to help
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
});
